import { useState } from 'react';
import { Check, Info, Minus, Plus } from 'lucide-react';
import classNames from 'classnames';
import { ReebeloConditions } from '@lambda/reebelo';
import i18n from '@/settings/i18n';
import { FilterProps } from './types';
import { useCollectionPageContext } from '../../hooks/useCollectionPageContext';
import { FilterType } from '../../hooks/types';
import { ConditionPopup } from '@/components/products/conditionPopup/ConditionPopup';
import settings from '@/settings';
import { generateVariantNameLabel } from '@/components/v2/product/sections/main/right/variants';

const t = i18n.init('collections');

const isNaStore = ['reebelo-us', 'reebelo-ca'].includes(settings.store);

const Filter = ({
  options,
  type,
  name,
  slug,
  expanded = false,
}: FilterProps) => {
  const {
    toggleFilter,
    toggleVariantFilter,
    selectedFilters,
    fixedFilters,
    filters,
    pk,
  } = useCollectionPageContext();

  const [isExpanded, setIsExpanded] = useState(expanded);
  const [isConditionOpen, setIsConditionOpen] = useState(false);

  if (options.length === 0) {
    return null;
  }

  const isSelected = (id: string, option: string) => {
    if (type === FilterType.VARIANT) {
      return selectedFilters?.variants[id]?.some((f) => f === option);
    }

    return selectedFilters?.[type]?.some((f) => f.id === id);
  };

  const streamLinedName = generateVariantNameLabel(name, slug ?? '');

  return (
    <div className="border-t border-gray-500 last:border-b lg:last:border-b-0">
      <div>
        <button
          className="flex h-full w-full items-center justify-between gap-1 p-4 text-sm font-bold uppercase leading-none underline-offset-2 hover:underline focus:outline-none"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-x-1">
            <h3>{t(streamLinedName)}</h3>
            {slug === 'condition' && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsConditionOpen(true);
                }}
                className="cursor-pointer focus:outline-none"
              >
                <Info className="h-4 w-4" />
              </span>
            )}
          </div>
          {isExpanded ? (
            <Minus className="-mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
          ) : (
            <Plus className="-mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
          )}
        </button>
      </div>
      <div
        className={classNames(
          'overflow-auto transition-all duration-150 ease-in-out',
          {
            // We need a number in order for the animation to work
            'max-h-[250px]': isExpanded,
            'max-h-0': !isExpanded,
          },
        )}
      >
        {options.map((option) => {
          const isOptionSelected = isSelected(option.id, option.name);
          const optionLabel = option.displayValue ?? option.name;

          return (
            <button
              onClick={() => {
                if (type === FilterType.VARIANT) {
                  toggleVariantFilter(option.id, option.name);
                } else {
                  toggleFilter(type, option.id, option.name);
                }
              }}
              key={`${option.id}_${option.name}`}
              className="group group flex w-full cursor-pointer select-none items-start gap-2 px-4 py-1.5 text-left text-sm leading-none first:pt-0 last:mb-2.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div
                className={classNames(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-gray-700/30 group-hover:border-gray-700',
                  {
                    'border-gray-700 bg-gray-700 text-white': isOptionSelected,
                    'border-gray-300 bg-white': !isOptionSelected,
                  },
                )}
              >
                {isOptionSelected && (
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                )}
              </div>

              <span className="leading-tight underline-offset-2 group-hover:underline">
                {optionLabel}
              </span>
            </button>
          );
        })}
      </div>
      <ConditionPopup
        activeCondition={isNaStore ? ReebeloConditions.LikeNew : 'Brand New'}
        isOpen={isConditionOpen}
        onClose={() => setIsConditionOpen(false)}
        // Conditions accept either identifier while the reebeloId cut-over completes.
        // PK -> Fixed CategoryPK -> Fixed Category -> First Available Category -> Default
        categoryId={
          pk ??
          fixedFilters?.categoryPks?.[0] ??
          fixedFilters?.categories?.[0] ??
          filters.categories?.[0]?.name ??
          'default'
        }
      />
    </div>
  );
};

export default Filter;
